import { motion } from "framer-motion";
import { Star, Instagram, BookOpen, Globe, Phone, Nfc } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import PhoneMockup from "@/components/PhoneMockup";

const STEPS = [
  { label: "Tap", desc: "Avvicina la card allo smartphone" },
  { label: "Scan", desc: "Il chip NFC viene letto all'istante" },
  { label: "Connect", desc: "Il cliente arriva dove vuoi tu" },
];

const DESTINATIONS = [
  { icon: Star, label: "Google Reviews" },
  { icon: Instagram, label: "Instagram" },
  { icon: BookOpen, label: "Menu Digitale" },
  { icon: Globe, label: "Sito Web" },
  { icon: Phone, label: "Contatti" },
];

const NfcShowcase = () => (
  <section id="nfc" data-testid="nfc-section" className="relative overflow-hidden py-28 lg:py-40">
    <div className="pointer-events-none absolute left-1/2 top-1/2 h-[700px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#C19D60]/[0.04] blur-[140px]" aria-hidden="true" />

    <div className="mx-auto grid max-w-[1440px] items-center gap-20 px-6 lg:grid-cols-2 lg:px-12">
      <Reveal className="relative order-2 lg:order-1">
        <div className="relative mx-auto max-w-[540px] pt-4 sm:pb-80" data-testid="nfc-visual">
          <motion.div
            initial={{ opacity: 0, rotate: 12, y: -20 }}
            whileInView={{ opacity: 1, rotate: 6, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-0 z-0 w-[58%]"
          >
            <img src="/assets/card-instagram-black.jpg" alt="Card NFC Recensio — Seguici su Instagram" className="w-full rounded-2xl shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9)]" />
          </motion.div>
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            className="relative z-10 mt-[16%] w-[66%] -rotate-2"
          >
            <img src="/assets/card-google-black.jpg" alt="Card NFC Recensio — Lascia una recensione su Google" className="w-full rounded-2xl border border-[#C19D60]/20 shadow-[0_50px_100px_-25px_rgba(0,0,0,0.95)]" />
          </motion.div>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            className="absolute bottom-0 right-0 z-20 hidden sm:block"
          >
            <PhoneMockup />
          </motion.div>
          <div className="absolute bottom-[34%] right-[16%] z-10 hidden sm:block" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="absolute left-0 top-0 h-28 w-28 rounded-full border border-[#C19D60]/50"
                style={{ animation: `nfc-ring 2.8s ease-out ${i * 0.9}s infinite` }}
              />
            ))}
            <Nfc size={20} className="absolute -left-2.5 -top-2.5 text-[#E5CAA4]" strokeWidth={1.2} />
          </div>
        </div>
      </Reveal>

      <div className="order-1 lg:order-2">
        <Reveal>
          <Overline className="mb-6">Soluzioni NFC</Overline>
          <h2 className="font-display text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            Un semplice gesto.<br /><span className="gold-text italic">Un'esperienza digitale.</span>
          </h2>
        </Reveal>

        <div className="mt-14 space-y-0">
          {STEPS.map((s, i) => (
            <Reveal key={s.label} delay={0.12 * i}>
              <div data-testid={`nfc-step-${s.label.toLowerCase()}`} className="group flex items-baseline gap-6 border-b border-[#C19D60]/10 py-6">
                <span className="font-display text-3xl font-light italic text-[#C19D60]/50 transition-colors duration-500 group-hover:text-[#C19D60]">
                  0{i + 1}
                </span>
                <div>
                  <h3 className="font-display text-2xl font-medium uppercase tracking-wide text-[#F8F5F0]">{s.label}</h3>
                  <p className="mt-1 text-sm font-light text-[#A3A3A3]">{s.desc}</p>
                </div>
                {i < 2 && <motion.span className="ml-auto h-px w-10 bg-[#C19D60]/30" aria-hidden="true" />}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.4}>
          <p className="mt-12 text-[11px] uppercase tracking-[0.3em] text-[#A3A3A3]">Dove può arrivare il cliente</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {DESTINATIONS.map((d) => (
              <span
                key={d.label}
                data-testid={`nfc-destination-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center gap-2.5 border border-[#C19D60]/25 bg-[#0F0F11]/80 px-4 py-2.5 text-xs font-light tracking-wide text-[#E5CAA4] transition-colors duration-500 hover:border-[#C19D60]/60 hover:bg-[#151517]"
              >
                <d.icon size={14} strokeWidth={1.4} className="text-[#C19D60]" /> {d.label}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.5}>
          <p className="mt-10 text-[11px] uppercase tracking-[0.3em] text-[#A3A3A3]">Disponibili anche in finitura bianca</p>
          <div className="mt-5 flex gap-4">
            <img src="/assets/card-google-white.jpg" alt="Card NFC Recensio bianca — Google" data-testid="nfc-card-white-google" className="w-40 rounded-lg border border-white/10 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.8)] transition-transform duration-700 hover:-translate-y-1.5" />
            <img src="/assets/card-instagram-white.jpg" alt="Card NFC Recensio bianca — Instagram" data-testid="nfc-card-white-instagram" className="w-40 rounded-lg border border-white/10 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.8)] transition-transform duration-700 hover:-translate-y-1.5" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default NfcShowcase;
